import React, { Suspense, useEffect, useState } from 'react';
import { clearSessionStorage } from '@/lib/mfa-trust';
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from 'react-router-dom';

import './App.css';

import { useEcommerce, useOrgSettings } from '@/hooks/use-settings';
import { useReportsV2 } from '@/lib/reports-v2';
import { setWorkspaceDisplay, workspaceCurrency, workspaceZone } from '@/lib/workspace-display';
import { needsWelcome } from '@/lib/workspace-presets';
import Login from './Login';
import ResetPassword from './ResetPassword';

import { AppShell } from '@/components/layout/app-shell';
import { PageFrameSkeleton } from '@/components/ui/skeleton-kit';
import { PlaceholderPage } from '@/pages/placeholder';
import { inMemberSession, memberInfo } from '@/lib/agencyMember';
import OAuthGoogleCallback from '@/pages/oauth-google-callback';
import OAuthMicrosoftCallback from '@/pages/oauth-microsoft-callback';
import OAuthSnapchatCallback from '@/pages/oauth-snapchat-callback';
import OAuthLinkedInCallback from '@/pages/oauth-linkedin-callback';

import { QueryProvider, queryClient } from '@/providers/query-provider';
import { ThemeProvider } from '@/providers/theme-provider';
import { api } from '@/lib/api';
import { canAccessPath, roleHome } from '@/lib/permissions';
import { fromDestinationsLink } from '@/lib/integrations-catalog';

// Lazy import that SELF-HEALS a failed chunk load. After a deploy, a tab that
// still holds an old chunk manifest can 404 on a lazy chunk (ChunkLoadError).
// Instead of surfacing the error card, retry the import a couple of times
// (transient CDN/network), then reload ONCE to pull the fresh manifest. The
// 30s cap means a genuinely-broken chunk can't loop the page - it falls through
// to throw, and the route error boundary shows its recoverable card.
function lazyWithRetry(factory) {
  return React.lazy(async () => {
    try {
      return await factory();
    } catch (err) {
      for (let i = 0; i < 2; i++) {
        await new Promise((r) => setTimeout(r, 350));
        try { return await factory(); } catch (_) { /* keep retrying */ }
      }
      try {
        const KEY = 'mm-chunk-reload';
        const last = Number(sessionStorage.getItem(KEY) || 0);
        if (!last || Date.now() - last > 30000) {
          sessionStorage.setItem(KEY, String(Date.now()));
          window.location.reload();
          return await new Promise(() => {}); // hold render until the reload happens
        }
      } catch (_) { /* sessionStorage blocked - fall through to throw */ }
      throw err;
    }
  });
}

// The CRM pages (pipeline, contacts, inbox...) used to ship in the main bundle,
// so a store owner opening Command Center downloaded and parsed a kanban board,
// a drag-and-drop library and a WhatsApp inbox first. They load on demand now
// like everything else; a lead workspace gets them warmed in idle time after
// its first page (lib/route-preload warmFavourites), and on sidebar hover.
const Dashboard = lazyWithRetry(() => import('./Dashboard'));
const LeadsKanban = lazyWithRetry(() => import('@/pages/leads-kanban'));
const PipelinesPage = lazyWithRetry(() => import('@/pages/pipelines-page'));
const LeadsArchive = lazyWithRetry(() => import('@/pages/leads-archive'));
const ContactsPage = lazyWithRetry(() => import('@/pages/contacts-page'));
const ContactDetailPage = lazyWithRetry(() => import('@/pages/contact-detail-page'));
// One contact (a person) with every deal it holds and its own stage (crm.deals_mode workspaces).
const CrmContactPage = lazyWithRetry(() => import('@/pages/crm-contact-page'));
const FollowupsPage = lazyWithRetry(() => import('@/pages/followups-page'));
const CallingListsPage = lazyWithRetry(() => import('@/pages/calling-lists-page'));
const MeetingsPage = lazyWithRetry(() => import('@/pages/meetings-page'));
const TasksPage = lazyWithRetry(() => import('@/pages/tasks-page'));
const WhatsAppInboxPage = lazyWithRetry(() => import('@/pages/whatsapp-inbox-page'));
const AgencyConsole = lazyWithRetry(() => import('@/pages/agency-console'));
const LinksPage = lazyWithRetry(() => import('@/pages/links-page'));
const Signup = lazyWithRetry(() => import('@/pages/signup'));
const Welcome = lazyWithRetry(() => import('@/pages/welcome'));
const SetupPage = lazyWithRetry(() => import('@/pages/setup'));
const StartRedirect = lazyWithRetry(() => import('@/pages/setup').then((m) => ({ default: m.StartRedirect })));
const InstallGuide = lazyWithRetry(() => import('@/pages/install-guide'));
const VerifyEmail = lazyWithRetry(() => import('@/pages/verify-email'));
const AcceptInvite = lazyWithRetry(() => import('@/pages/accept-invite'));

// Lazy: heavier / less-frequently-first pages load on demand and are cached
// immutably after the first visit. Shrinks the initial download; the app shell
// shows a brief loader (sidebar/topbar stay put) only that first time.
const CalendarPage = lazyWithRetry(() => import('@/pages/calendar-page'));
const CallsPage = lazyWithRetry(() => import('@/pages/calls-page'));
const CohortsPage = lazyWithRetry(() => import('@/pages/cohorts-page'));
const StudentsPage = lazyWithRetry(() => import('@/pages/students-page'));
const FinancePage = lazyWithRetry(() => import('@/pages/finance-page'));
const ExpensesPage = lazyWithRetry(() => import('@/pages/expenses-page'));
const AutomationsPage = lazyWithRetry(() => import('@/pages/automations-page'));
// The workflow knowledge base. Gated with /automations, because anyone who can
// open the builder should be able to read what its options mean.
const WorkflowGuidePage = lazyWithRetry(() => import('@/pages/workflow-guide-page'));
// The builder, full-page. Same editor component the list still opens in a
// dialog - see automation-editor-page.jsx.
const AutomationEditorPage = lazyWithRetry(() => import('@/pages/automation-editor-page'));
const ScoringPage = lazyWithRetry(() => import('@/pages/scoring-page'));
const FormsPage = lazyWithRetry(() => import('@/pages/forms-page'));
const ColdCapturesPage = lazyWithRetry(() => import('@/pages/cold-captures-page'));
const DealsPage = lazyWithRetry(() => import('@/pages/deals-page'));
const OfferingsPage = lazyWithRetry(() => import('@/pages/offerings-page'));
const CommerceDashboard = lazyWithRetry(() => import('@/pages/commerce-dashboard'));
const SiteScan = lazyWithRetry(() => import('@/pages/site-scan'));
const ServerContainerPage = lazyWithRetry(() => import('@/pages/server-container'));
const AddFeaturesPage = lazyWithRetry(() => import('@/pages/add-features'));
const CommerceOrders = lazyWithRetry(() => import('@/pages/commerce-orders'));
const CommerceCustomers = lazyWithRetry(() => import('@/pages/commerce-customers'));
const CommerceAttribution = lazyWithRetry(() => import('@/pages/commerce-attribution'));
const CommerceProducts = lazyWithRetry(() => import('@/pages/commerce-products'));
const CommerceCosts = lazyWithRetry(() => import('@/pages/commerce-costs'));
const CommerceUpsells = lazyWithRetry(() => import('@/pages/commerce-upsells'));
const CommerceRecovery = lazyWithRetry(() => import('@/pages/commerce-recovery'));
const CommerceAbandoned = lazyWithRetry(() => import('@/pages/commerce-abandoned'));
const CommerceRecs = lazyWithRetry(() => import('./pages/commerce-recs'));
const CommerceRecsRules = lazyWithRetry(() => import('./pages/commerce-recs-rules'));
const GrowthCommandCenterV2 = lazyWithRetry(() => import('./pages/growth-command-center-v2'));
const GrowthActions = lazyWithRetry(() => import('./pages/growth-actions'));
const GrowthCustomerValue = lazyWithRetry(() => import('./pages/growth-customer-value'));
const GrowthChannelRoles = lazyWithRetry(() => import('./pages/growth-channel-roles'));
const GrowthLeaks = lazyWithRetry(() => import('./pages/growth-leaks'));
const SignalStudio = lazyWithRetry(() => import('./pages/signal-studio'));
// Development only: the tracking goal's screens from fixtures (src/dev). The
// condition is a build-time constant, so the production build drops the route
// and the file with it.
const GoalHarness = process.env.NODE_ENV === 'development' ? React.lazy(() => import('./dev/goal-harness')) : null;
const JourneyHarness = process.env.NODE_ENV === 'development' ? React.lazy(() => import('./dev/journey-harness')) : null;
const CrmHarness = process.env.NODE_ENV === 'development' ? React.lazy(() => import('./dev/crm-harness')) : null;
const SignalHarness = process.env.NODE_ENV === 'development' ? React.lazy(() => import('./dev/signal-harness')) : null;
const LeadsHarness = process.env.NODE_ENV === 'development' ? React.lazy(() => import('./dev/leads-harness')) : null;
const MarksHarness = process.env.NODE_ENV === 'development' ? React.lazy(() => import('./dev/marks-harness')) : null;
const SgtmHarness = process.env.NODE_ENV === 'development' ? React.lazy(() => import('./dev/sgtm-harness')) : null;
const MetaCreditedHarness = process.env.NODE_ENV === 'development' ? React.lazy(() => import('./dev/meta-credited-harness')) : null;
const BudgetPlanHarness = process.env.NODE_ENV === 'development' ? React.lazy(() => import('./dev/budget-plan-harness')) : null;
const SetupLeadsHarness = process.env.NODE_ENV === 'development' ? React.lazy(() => import('./dev/setup-leads-harness')) : null;
const MetaReferenceHarness = process.env.NODE_ENV === 'development' ? React.lazy(() => import('./dev/meta-reference-harness')) : null;
const FindingsHarness = process.env.NODE_ENV === 'development' ? React.lazy(() => import('./dev/findings-harness')) : null;
const GrowthActionsHarness = process.env.NODE_ENV === 'development' ? React.lazy(() => import('./dev/growth-actions-harness')) : null;
const ServerGtmHarness = process.env.NODE_ENV === 'development' ? React.lazy(() => import('./dev/server-gtm-harness')) : null;
const OrdersHarness = process.env.NODE_ENV === 'development' ? React.lazy(() => import('./dev/orders-harness')) : null;
const TodayHarness = process.env.NODE_ENV === 'development' ? React.lazy(() => import('./dev/today-harness')) : null;
const ValidationHarness = process.env.NODE_ENV === 'development' ? React.lazy(() => import('./dev/validation-harness')) : null;
const SegmentsHarness = process.env.NODE_ENV === 'development' ? React.lazy(() => import('./dev/segments-harness')) : null;
const BuyersCardHarness = process.env.NODE_ENV === 'development' ? React.lazy(() => import('./dev/buyers-card-harness')) : null;
const SentEventsHarness = process.env.NODE_ENV === 'development' ? React.lazy(() => import('./dev/sent-events-harness')) : null;
const GoalsHarness = process.env.NODE_ENV === 'development' ? React.lazy(() => import('./dev/goals-harness')) : null;
const CampaignsHarness = process.env.NODE_ENV === 'development' ? React.lazy(() => import('./dev/campaigns-harness')) : null;
const ProductsHarness = process.env.NODE_ENV === 'development' ? React.lazy(() => import('./dev/products-harness')) : null;
const SaleSeason = lazyWithRetry(() => import('./pages/sale-season'));
const NumberCheck = lazyWithRetry(() => import('./pages/number-check'));
const ValueReport = lazyWithRetry(() => import('./pages/value-report'));
const GrowthFindings = lazyWithRetry(() => import('./pages/growth-findings'));
const GrowthGuide = lazyWithRetry(() => import('./pages/growth-guide'));
const GrowthCampaigns = lazyWithRetry(() => import('./pages/growth-campaigns'));
// An old address lives on as a redirect that keeps its query (the level).
// `to` may carry a query of its own (the tab that replaced a retired page);
// the old URL's params ride along and the target's own keys win.
function RedirectKeepQuery({ to }) {
  const loc = useLocation();
  const [path, own = ''] = to.split('?');
  const q = new URLSearchParams(loc.search);
  for (const [k, v] of new URLSearchParams(own)) q.set(k, v);
  const qs = q.toString();
  return <Navigate to={`${path}${qs ? `?${qs}` : ''}`} replace />;
}
// The Destinations page retired on 29 Sep 2026: every card it held opens in
// the Integrations drawer. Its old links (?d=<card>, and the two Google consent
// returns) land on that card. A rendered <Navigate>, never an effect, so nothing
// can race it; the one read it needs (store or lead business, for Audiences)
// waits on the same settings query the shell has already made.
function DestinationsRedirect() {
  const loc = useLocation();
  const { enabled: ecomOn, isLoading } = useEcommerce();
  if (isLoading && new URLSearchParams(loc.search).get('d') === 'audiences') return <PageFrameSkeleton />;
  return <Navigate to={fromDestinationsLink(loc.search, { ecomOn })} replace />;
}
const GrowthBuyers = lazyWithRetry(() => import('./pages/growth-buyers'));
// The new reports (lib/reports-v2): one explorer per thing you slice by.
const MarketingExplorer = lazyWithRetry(() => import('./pages/reports/marketing'));
const ChannelsExplorer = lazyWithRetry(() => import('./pages/reports/channels'));
const ProductsExplorer = lazyWithRetry(() => import('./pages/reports/products'));
const CustomersExplorer = lazyWithRetry(() => import('./pages/reports/customers'));
const LeadsExplorer = lazyWithRetry(() => import('./pages/reports/leads'));
const FoundHub = lazyWithRetry(() => import('./pages/reports/found'));

// WHERE AN OLD ADDRESS LANDS while the new reports are on. Every link,
// bookmark and "see also" in the product still names today's pages; with the
// new reports on, each lands on its new home with its query (dates, filters)
// intact, so nothing has to be rewritten for the move and nothing dead-ends.
// With them off the old page renders exactly as before.
const NEW_HOME = {
  '/growth/campaigns': (sp) => {
    const level = sp.get('level');
    const tab = sp.get('tab');
    sp.delete('tab');
    if (level === 'platform') { sp.delete('level'); return '/reports/marketing/overview'; }
    return `/reports/marketing/${['funnel', 'return', 'customers', 'scale', 'reach'].includes(tab) ? tab : 'funnel'}`;
  },
  '/growth/creatives': () => '/reports/marketing/creative',
  '/commerce/attribution': () => '/reports/channels/sales',
  '/growth/channel-roles': () => '/reports/channels/roles',
  '/growth/funnels': (sp) => {
    const view = sp.get('view');
    sp.delete('view');
    if (view === 'product') return '/reports/products/funnel';
    sp.set('part', view === 'leaks' ? 'leaks' : 'store');
    return '/reports/channels/funnel';
  },
  '/growth/buyers': () => '/reports/customers/buyers',
  '/growth/customer-value': () => '/reports/customers/value',
  '/commerce/segments': () => '/reports/customers/segments',
  '/growth/findings': () => '/reports/found/findings',
  '/growth/leaks': () => '/reports/found/leaks',
  '/tracking/attribution': (sp) => {
    const tab = sp.get('tab');
    sp.delete('tab');
    if (tab === 'repeat') return '/tracking/repeat-clicks';
    return `/reports/leads/${['channels', 'campaigns', 'quality', 'leads', 'journeys'].includes(tab) ? tab : 'channels'}`;
  },
};
const INSIGHT_HOME = { product: '/reports/products/sales', catalog: '/reports/products/catalog', channels: '/reports/channels/sales',
  funnel: '/reports/channels/funnel?part=channel', timing: '/reports/channels/timing', arrivals: '/reports/channels/landing' };

function NewHome({ path, children }) {
  const { on } = useReportsV2();
  const loc = useLocation();
  const params = useParams();
  if (!on) return children;
  const sp = new URLSearchParams(loc.search);
  let to = null;
  if (path === '/commerce/insights/:tab') to = INSIGHT_HOME[params.tab] || null;
  else if (NEW_HOME[path]) to = NEW_HOME[path](sp);
  if (!to) return children;
  const [p, own = ''] = to.split('?');
  for (const [k, v] of new URLSearchParams(own)) sp.set(k, v);
  const qs = sp.toString();
  return <Navigate to={`${p}${qs ? `?${qs}` : ''}`} replace />;
}
const GrowthAudienceBuilder = lazyWithRetry(() => import('./pages/growth-audience-builder'));
const GrowthFunnels = lazyWithRetry(() => import('./pages/growth-funnels'));
const GrowthCreatives = lazyWithRetry(() => import('./pages/growth-creatives'));
const CommerceRecoveryEmail = lazyWithRetry(() => import('@/pages/commerce-recovery-email'));
const CommerceInsights = lazyWithRetry(() => import('@/pages/commerce-insights'));
const CommerceSegments = lazyWithRetry(() => import('@/pages/commerce-segments'));
const AudienceLists = lazyWithRetry(() => import('@/pages/audience-lists'));
const CommerceOrderDetail = lazyWithRetry(() => import('@/pages/commerce-order-detail'));
const CommerceCustomerDetail = lazyWithRetry(() => import('@/pages/commerce-customer-detail'));
const PopupsPage = lazyWithRetry(() => import('@/pages/popups-page'));
const ChatPage = lazyWithRetry(() => import('@/pages/chat-page'));
const AttributionPage = lazyWithRetry(() => import('@/pages/attribution-page'));
const RepeatClicksPage = lazyWithRetry(() => import('@/pages/repeat-clicks-page'));
const InvalidClickClaim = lazyWithRetry(() => import('@/pages/invalid-click-claim'));
const ConversionsPage = lazyWithRetry(() => import('@/pages/conversions-page'));
const EmailAnalyticsPage = lazyWithRetry(() => import('@/pages/email-analytics-page'));
const EventHealthPage = lazyWithRetry(() => import('@/pages/event-health-page'));
const EventStreamPage = lazyWithRetry(() => import('@/pages/event-stream'));
const SettingsPage = lazyWithRetry(() => import('@/pages/settings-page'));
const IntegrationsPage = lazyWithRetry(() => import('@/pages/integrations-page'));
const IntegrationDetailPage = lazyWithRetry(() => import('@/pages/integrations-page').then((m) => ({ default: m.IntegrationDetailPage })));
const WhatsAppSetupPage = lazyWithRetry(() => import('@/pages/whatsapp-setup-page'));
const ShopifySetupPage = lazyWithRetry(() => import('@/pages/shopify-setup-page'));
const WhatsAppBroadcastsPage = lazyWithRetry(() => import('@/pages/whatsapp-broadcasts-page'));
const EmailTemplatesPage = lazyWithRetry(() => import('@/pages/email-templates-page'));
const WorkflowEmailsPage = lazyWithRetry(() => import('@/pages/workflow-emails-page'));
const WhatsAppTemplatesPage = lazyWithRetry(() => import('@/pages/whatsapp-templates-page'));
const WhatsAppFlowsPage = lazyWithRetry(() => import('@/pages/whatsapp-flows-page'));
const SequencesPage = lazyWithRetry(() => import('@/pages/sequences-page'));
const WebhooksPage = lazyWithRetry(() => import('@/pages/webhooks-page'));
const SuperadminRoot = lazyWithRetry(() => import('@/pages/superadmin'));

// Home lands on the Growth Command Center for e-commerce workspaces - the
// business view - and the lead view for everyone else. The old purchase-
// attribution landing moved under Measurement as Signal Health: the trust
// layer belongs one click away, not in the founder's first screen.
function HomeDashboard() {
  const { enabled: ecom, isLoading } = useEcommerce();
  // The page's outline while the workspace's settings say which home it is.
  if (isLoading) return <PageFrameSkeleton />;
  return ecom ? <GrowthCommandCenterV2 /> : <Dashboard />;
}

// Per-role route gating. `gate` is the base nav path checked against the
// role's allowed paths (lib/permissions). A route the role can't open
// redirects to that role's home, so a hand-typed URL can't bypass the nav.
const APP_ROUTES = [
  { path: '/dashboard',                  gate: '/dashboard',                  el: <HomeDashboard /> },
  // The preview URL from the approval round redirects to the real home.
  { path: '/dashboard/preview',          gate: '/dashboard',                  el: <Navigate to="/dashboard" replace /> },
  { path: '/leads',                      gate: '/leads',                      el: <LeadsKanban /> },
  { path: '/pipelines',                  gate: '/leads',                      el: <PipelinesPage /> },
  { path: '/leads/archive',              gate: '/leads',                      el: <LeadsArchive /> },
  { path: '/contacts',                   gate: '/contacts',                   el: <ContactsPage /> },
  { path: '/contacts/person/:id',        gate: '/contacts',                   el: <CrmContactPage /> },
  { path: '/contacts/:id',               gate: '/contacts',                   el: <ContactDetailPage /> },
  { path: '/meetings',                   gate: '/meetings',                   el: <MeetingsPage /> },
  { path: '/calls',                      gate: '/calls',                      el: <CallsPage /> },
  { path: '/follow-ups',                 gate: '/follow-ups',                 el: <FollowupsPage /> },
  // The sales team's daily lists of contacts to call (GirOrganic's CRM, phase 2).
  { path: '/calling-lists',              gate: '/contacts',                   el: <CallingListsPage /> },
  { path: '/tasks',                      gate: '/tasks',                      el: <TasksPage /> },
  { path: '/calendar',                   gate: '/calendar',                   el: <CalendarPage /> },
  { path: '/whatsapp',                   gate: '/whatsapp',                   el: <WhatsAppInboxPage /> },
  { path: '/whatsapp/broadcasts',        gate: '/whatsapp',                   el: <WhatsAppBroadcastsPage /> },
  { path: '/automations',                gate: '/automations',                el: <AutomationsPage /> },
  { path: '/automations/guide',          gate: '/automations',                el: <WorkflowGuidePage /> },
  { path: '/automations/new',            gate: '/automations',                el: <AutomationEditorPage /> },
  { path: '/automations/:id/edit',       gate: '/automations',                el: <AutomationEditorPage /> },
  { path: '/scoring',                    gate: '/scoring',                    el: <ScoringPage /> },
  { path: '/forms',                      gate: '/forms',                      el: <FormsPage /> },
  { path: '/cold-captures',              gate: '/cold-captures',              el: <ColdCapturesPage /> },
  { path: '/deals',                      gate: '/deals',                      el: <DealsPage /> },
  { path: '/offerings',                  gate: '/offerings',                  el: <OfferingsPage /> },
  { path: '/commerce',                   gate: '/commerce/orders',                   el: <CommerceOrders /> },
  { path: '/commerce/customers',         gate: '/commerce/customers',                   el: <CommerceCustomers /> },
  { path: '/commerce/attribution',       gate: '/commerce/attribution',                   el: <CommerceAttribution /> },
  { path: '/commerce/insights',          gate: '/commerce/insights',                   el: <CommerceInsights /> },
  { path: '/commerce/insights/:tab',     gate: '/commerce/insights',                   el: <CommerceInsights /> },
  { path: '/commerce/segments',          gate: '/commerce/segments',                   el: <CommerceSegments /> },
  // Every list on every platform and account; rides the segments permission.
  { path: '/commerce/audiences',         gate: '/commerce/segments',                   el: <AudienceLists /> },
  { path: '/commerce/products',          gate: '/commerce/orders',                   el: <CommerceProducts /> },
  { path: '/commerce/costs',             gate: '/commerce/costs',                   el: <CommerceCosts /> },
  { path: '/commerce/upsells',           gate: '/commerce/insights',                el: <CommerceUpsells /> },
  { path: '/commerce/actions',           gate: '/growth/actions',                   el: <Navigate to="/growth/actions" replace /> },
  { path: '/commerce/abandoned',         gate: '/commerce/abandoned',                   el: <CommerceAbandoned /> },
  { path: '/commerce/recovery',          gate: '/commerce/recovery',                   el: <CommerceRecovery /> },
  { path: '/commerce/recommendations',   gate: '/commerce/recommendations',                   el: <CommerceRecs /> },
  { path: '/commerce/recommendations/rules', gate: '/commerce/recommendations',               el: <CommerceRecsRules /> },
  { path: '/commerce/recommendations/rules/:rule', gate: '/commerce/recommendations',         el: <CommerceRecsRules /> },
  { path: '/growth/actions',             gate: '/growth/actions',                   el: <GrowthActions /> },
  { path: '/growth/customer-value',      gate: '/growth/customer-value',                   el: <GrowthCustomerValue /> },
  { path: '/growth/leaks',               gate: '/growth/leaks',                   el: <GrowthLeaks /> },
  // A lead desk opens it for the events matrix, with Destinations access.
  { path: '/studio',                     gate: ['/studio', '/tracking/destinations'], el: <SignalStudio /> },
  { path: '/sale-season',                gate: '/sale-season',                   el: <SaleSeason /> },
  { path: '/growth/channel-roles',       gate: '/growth/channel-roles',                   el: <GrowthChannelRoles /> },
  { path: '/growth/guide',               gate: '/growth/guide',                   el: <GrowthGuide /> },
  { path: '/growth/findings',            gate: '/growth/findings',                   el: <GrowthFindings /> },
  { path: '/growth/campaigns',           gate: '/growth/campaigns',                   el: <GrowthCampaigns /> },
  { path: '/growth/audiences',           gate: '/growth/campaigns',                   el: <RedirectKeepQuery to="/growth/campaigns" /> },
  { path: '/growth/buyers',              gate: '/growth/buyers',                   el: <GrowthBuyers /> },
  { path: '/growth/audience-builder',    gate: '/growth/campaigns',                   el: <GrowthAudienceBuilder /> },
  // Profit & Economics retired 24 Sep 2026: campaign economics had already
  // moved to Campaigns, product profitability joined Insights > Product.
  { path: '/growth/economics',           gate: '/commerce/insights',                   el: <Navigate to="/commerce/insights/product" replace /> },
  { path: '/growth/funnels',             gate: '/growth/funnels',                   el: <GrowthFunnels /> },
  { path: '/growth/creatives',           gate: '/growth/creatives',                   el: <GrowthCreatives /> },
  { path: '/signal-health',              gate: '/signal-health',                   el: <CommerceDashboard /> },
  { path: '/number-check',               gate: '/number-check',                    el: <NumberCheck /> },
  { path: '/value-report',               gate: '/value-report',                    el: <ValueReport /> },
  { path: '/tracking/site-scan',         gate: '/tracking/site-scan',                   el: <SiteScan /> },
  { path: '/server-container',           gate: '/server-container',           el: <ServerContainerPage /> },
  // A focused workspace's cards (lib/workspace-modules); rides Server GTM's permission.
  { path: '/add-features',               gate: '/server-container',           el: <AddFeaturesPage /> },
  // The Command Center IS the dashboard now - the old growth URL stays alive
  // for links, footers and habit, but there is only one page.
  { path: '/growth',                     gate: '/growth/guide',                   el: <Navigate to="/dashboard" replace /> },
  { path: '/commerce/recovery/email',    gate: '/commerce/recovery/email',                   el: <CommerceRecoveryEmail /> },
  { path: '/commerce/orders/:id',        gate: '/commerce/orders',                   el: <CommerceOrderDetail /> },
  { path: '/commerce/customers/:id',     gate: '/commerce/customers',                   el: <CommerceCustomerDetail /> },
  { path: '/popups',                     gate: '/popups',                     el: <PopupsPage /> },
  { path: '/chat',                       gate: '/chat',                       el: <ChatPage /> },
  { path: '/automation/email-templates', gate: '/automation/email-templates', el: <EmailTemplatesPage /> },
  { path: '/automation/workflow-emails',  gate: '/automation/workflow-emails', el: <WorkflowEmailsPage /> },
  { path: '/automation/whatsapp-templates', gate: '/automation/whatsapp-templates', el: <WhatsAppTemplatesPage /> },
  { path: '/automation/whatsapp-flows',     gate: '/automation/whatsapp-flows',     el: <WhatsAppFlowsPage /> },
  { path: '/automation/sequences',          gate: '/automation/sequences',          el: <SequencesPage /> },
  // Retired: the cards live in Integrations. The path stays as the permission key.
  { path: '/tracking/destinations',      gate: '/tracking/destinations',      el: <DestinationsRedirect /> },
  { path: '/tracking/webhooks',          gate: '/tracking/webhooks',          el: <WebhooksPage /> },
  { path: '/tracking/attribution',       gate: '/tracking/attribution',       el: <AttributionPage /> },
  { path: '/tracking/repeat-clicks',     gate: ['/commerce/attribution', '/tracking/attribution'], el: <RepeatClicksPage /> },
  { path: '/tracking/conversions',       gate: '/tracking/conversions',       el: <ConversionsPage /> },
  // Acquisition and Analytics were merged into Lead attribution (24 Sep 2026):
  // one channel rule instead of three. Old links land on the tab that replaced them.
  { path: '/tracking/acquisition',       gate: '/tracking/attribution',       el: <RedirectKeepQuery to="/tracking/attribution?tab=quality" /> },
  { path: '/tracking/email',             gate: '/tracking/email',             el: <EmailAnalyticsPage /> },
  { path: '/tracking/stream',            gate: '/tracking/stream',            el: <EventStreamPage /> },
  { path: '/tracking/health',            gate: '/tracking/health',            el: <EventHealthPage /> },
  { path: '/tracking/links',             gate: '/tracking/links',             el: <LinksPage /> },
  // The new reports. Each explorer rides the permission of the page its
  // numbers came from, so access is unchanged by the move.
  { path: '/reports/marketing',          gate: '/growth/campaigns',           el: <MarketingExplorer /> },
  { path: '/reports/marketing/:tab',     gate: '/growth/campaigns',           el: <MarketingExplorer /> },
  { path: '/reports/channels',           gate: '/commerce/attribution',       el: <ChannelsExplorer /> },
  { path: '/reports/channels/:tab',      gate: '/commerce/attribution',       el: <ChannelsExplorer /> },
  { path: '/reports/products',           gate: '/commerce/insights',          el: <ProductsExplorer /> },
  { path: '/reports/products/:tab',      gate: '/commerce/insights',          el: <ProductsExplorer /> },
  { path: '/reports/customers',          gate: '/growth/customer-value',      el: <CustomersExplorer /> },
  { path: '/reports/customers/:tab',     gate: '/growth/customer-value',      el: <CustomersExplorer /> },
  { path: '/reports/leads',              gate: '/tracking/attribution',       el: <LeadsExplorer /> },
  { path: '/reports/leads/:tab',         gate: '/tracking/attribution',       el: <LeadsExplorer /> },
  { path: '/reports/found',              gate: '/growth/findings',            el: <FoundHub /> },
  { path: '/reports/found/:tab',         gate: '/growth/findings',            el: <FoundHub /> },
  { path: '/students',                   gate: '/students',                   el: <StudentsPage /> },
  { path: '/cohorts',                    gate: '/cohorts',                    el: <CohortsPage /> },
  { path: '/finance',                    gate: '/finance',                    el: <FinancePage /> },
  { path: '/expenses',                   gate: '/expenses',                   el: <ExpensesPage /> },
  { path: '/analytics',                  gate: '/tracking/attribution',       el: <RedirectKeepQuery to="/tracking/attribution?tab=channels" /> },
  // Every connector in one place. Gated like Settings: a connector's drawer
  // is the same card, keys and all, that Settings shows.
  { path: '/integrations',               gate: ['/settings', '/tracking/destinations'], el: <IntegrationsPage /> },
  { path: '/integrations/:key',          gate: ['/settings', '/tracking/destinations'], el: <IntegrationDetailPage /> },
  // Every WhatsApp setting (was the WhatsApp tab in Settings). Same permission.
  { path: '/integrations/whatsapp',      gate: '/settings',                   el: <WhatsAppSetupPage /> },
  // A Shopify store's whole setup, and its checkout app. Same permission.
  { path: '/integrations/shopify',       gate: '/settings',                   el: <ShopifySetupPage /> },
  { path: '/settings',                   gate: '/settings',                   el: <SettingsPage /> },
  // The guided setup (19 Oct 2026): where /welcome ends and the sidebar's
  // "Setup 3 of 5" leads. Opens for anyone who could open Settings, as the
  // first-run page it replaces did. /start, its old address, lands on it.
  { path: '/setup',                      gate: ['/setup', '/settings'],       el: <SetupPage /> },
  { path: '/start',                      gate: ['/setup', '/settings'],       el: <StartRedirect /> },
];

function AuthedApp({ user, onLogout }) {
  const home = roleHome();
  // A new self-serve workspace owes /welcome its one question. Checked here,
  // on every signed-in load, because the pages that used to redirect there
  // (signup, the verify link) are interrupted by the mandatory second factor
  // and the login page that finishes it lands on home. A rendered <Navigate>,
  // not an effect, so nothing else can race it. Shares the ['org-settings']
  // query the shell already makes, so it costs no extra request.
  const { data: orgSettings } = useOrgSettings();
  // Every amount and timestamp is drawn in the workspace's currency and zone
  // (lib/workspace-display). Taken here, before any page renders; the key
  // redraws the pages once if they changed from what this browser last saw.
  setWorkspaceDisplay({
    currency: orgSettings?.display?.currency,
    timeZone: orgSettings?.display?.time_zone || orgSettings?.values?.['org.timezone'],
  });
  const displayKey = `${workspaceCurrency()}|${workspaceZone()}`;
  if (needsWelcome(orgSettings?.values, user?.role)) return <Navigate to="/welcome" replace />;
  return (
    <Routes key={displayKey}>
      <Route element={<AppShell user={user} onLogout={onLogout} />}>
        {APP_ROUTES.map(({ path, gate, el }) => (
          <Route
            key={path}
            path={path}
            element={[].concat(gate).some(canAccessPath)
              ? (NEW_HOME[path] || path === '/commerce/insights/:tab' ? <NewHome path={path}>{el}</NewHome> : el)
              : <Navigate to={home} replace />}
          />
        ))}

        {/* /team is a shortcut to Settings → Team tab */}
        <Route path="/team" element={<Navigate to="/settings?tab=team" replace />} />

        {/* OAuth landing pages */}
        <Route path="/oauth/google/callback" element={<OAuthGoogleCallback />} />
        <Route path="/oauth/microsoft/callback" element={<OAuthMicrosoftCallback />} />
        <Route path="/oauth/snapchat/callback" element={<OAuthSnapchatCallback />} />
        <Route path="/oauth/linkedin/callback" element={<OAuthLinkedInCallback />} />

        {/* Defaults: land on the role's home */}
        <Route path="/" element={<Navigate to={home} replace />} />
        <Route
          path="*"
          element={
            <PlaceholderPage
              title="Not found"
              description="The page you're looking for doesn't exist (yet)."
              phase="404"
            />
          }
        />
      </Route>
    </Routes>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { setLoading(false); return; }

    // Optimistic: render from localStorage so there's no flash (role included
    // so the analyst gate applies immediately, before /me resolves).
    const cachedId = localStorage.getItem('userId');
    const cachedName = localStorage.getItem('userName');
    const cachedRole = localStorage.getItem('userRole') || '';
    // For an agency member inside a workspace, the per-workspace user row has an
    // internal placeholder email + the member's name. Their REAL name/email live
    // in the agency session, so prefer those for display everywhere.
    const mem = inMemberSession() ? memberInfo() : null;
    if (cachedId && (cachedName || mem)) {
      setUser({ id: cachedId, name: mem?.name || cachedName, email: mem?.email, role: cachedRole });
      // Paint the shell now rather than after /me: the check below still
      // runs, corrects the id if it was stale, and signs out a dead token.
      // Waiting on it put a blank "Loading..." screen in front of every
      // reload and held back every report request by one round trip.
      setLoading(false);
    }

    // Verify + self-heal against the actual token. A stale userId left over
    // from a previous session (e.g. logged in as a different user) would
    // otherwise make every userId-keyed view (Dashboard, Calendar) show the
    // wrong person's data. /me decodes the JWT, so this is the source of truth.
    // Through the query cache, under the key the shell's impersonation banner
    // reads, so a load asks /me once rather than twice.
    queryClient.fetchQuery({
      queryKey: ['auth', 'me-shell'],
      queryFn: async () => (await api.get('/api/auth/me')).data,
      staleTime: 5 * 60_000,
      retry: false,
      meta: { silent: true },
    })
      .then((data) => {
        if (data?.id != null) {
          localStorage.setItem('userId', String(data.id));
          localStorage.setItem('userRole', data.role || '');
          if (data.permissions) localStorage.setItem('userPermissions', JSON.stringify(data.permissions));
          else localStorage.removeItem('userPermissions');
          setUser((u) => ({
            id: String(data.id),
            name: mem?.name || u?.name || cachedName || data.name || data.email,
            email: mem?.email || data.email,
            role: data.role,
          }));
        }
      })
      .catch(() => {
        // Invalid / expired token -> force re-login.
        clearSessionStorage();
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleLogout = () => {
    clearSessionStorage();
    setUser(null);
  };

  if (loading) {
    return <div className="app loading">Loading…</div>;
  }

  return (
    <ThemeProvider defaultTheme="light">
      <QueryProvider>
        {/* App lives under /app (marketing site owns the bare domain). All
            in-app <Link>/navigate paths stay root-relative; React Router
            prefixes this basename automatically. */}
        <Router basename="/app">
          {/* The pages outside the shell (signup, welcome, the agency console)
              load on demand; the shell has its own boundary for its pages. */}
          <Suspense fallback={<div className="app loading">Loading…</div>}>
          <Routes>
            {/* Platform console: its OWN login + session (platform_token),
                fully outside the tenant auth gate below. */}
            <Route path="/superadmin/*" element={<Suspense fallback={<div className="app loading">Loading…</div>}><SuperadminRoot /></Suspense>} />

            {/* Public self-serve signup - also outside the auth gate. If a
                signed-in tenant hits it, send them to their workspace. */}
            <Route path="/signup" element={user ? <Navigate to="/" replace /> : <Signup />} />
            {/* Install instructions for whoever edits the site, by a token in
                the link: readable signed in or not. */}
            <Route path="/install/:token" element={<Suspense fallback={<div className="app loading">Loading…</div>}><InstallGuide /></Suspense>} />

            {/* What this workspace is for, asked once AFTER the account exists.
                Reached only by redirect from signup or from the verify landing,
                never from the sidebar - a one-time flow is not a module, so it
                deliberately has no nav entry. It needs a session, so it sits
                inside the gate. */}
            <Route path="/welcome" element={user ? <Welcome /> : <Navigate to="/login" replace />} />

            {/* Google's redirect lands here for sign-in, Google signup and the
                platform email connect as well as the calendar connect, and the
                first three arrive with nobody signed in. Inside the gate only,
                a signed-out visitor got the login form and "Continue with
                Google" went nowhere. The page reads the state's kind itself. */}
            <Route path="/oauth/google/callback" element={<OAuthGoogleCallback />} />
            {/* LinkedIn sign-in lands here signed out, exactly like Google's. */}
            <Route path="/oauth/linkedin/callback" element={<OAuthLinkedInCallback />} />

            {/* The invalid click report prints on its own letterhead, so it
                sits outside the shell; it still needs a session. */}
            <Route path="/print/invalid-clicks" element={user ? <InvalidClickClaim /> : <Navigate to="/login" replace />} />

            {GoalHarness && <Route path="/__dev/tracking-goal" element={<React.Suspense fallback={null}><GoalHarness /></React.Suspense>} />}
            {JourneyHarness && <Route path="/__dev/journey-filter" element={<React.Suspense fallback={null}><JourneyHarness /></React.Suspense>} />}
            {CrmHarness && <Route path="/__dev/crm-inbound" element={<React.Suspense fallback={null}><CrmHarness /></React.Suspense>} />}
            {SignalHarness && <Route path="/__dev/signal-catalogue" element={<React.Suspense fallback={null}><SignalHarness /></React.Suspense>} />}
            {LeadsHarness && <Route path="/__dev/leads" element={<React.Suspense fallback={null}><LeadsHarness /></React.Suspense>} />}
            {MarksHarness && <Route path="/__dev/marks" element={<React.Suspense fallback={null}><MarksHarness /></React.Suspense>} />}
            {SgtmHarness && <Route path="/__dev/sgtm" element={<React.Suspense fallback={null}><SgtmHarness /></React.Suspense>} />}
            {MetaCreditedHarness && <Route path="/__dev/meta-credited" element={<React.Suspense fallback={null}><MetaCreditedHarness /></React.Suspense>} />}
            {BudgetPlanHarness && <Route path="/__dev/budget-plan" element={<React.Suspense fallback={null}><BudgetPlanHarness /></React.Suspense>} />}
            {SetupLeadsHarness && <Route path="/__dev/setup-leads" element={<React.Suspense fallback={null}><SetupLeadsHarness /></React.Suspense>} />}
            {MetaReferenceHarness && <Route path="/__dev/meta-reference" element={<React.Suspense fallback={null}><MetaReferenceHarness /></React.Suspense>} />}
            {FindingsHarness && <Route path="/__dev/findings" element={<React.Suspense fallback={null}><FindingsHarness /></React.Suspense>} />}
            {GrowthActionsHarness && <Route path="/__dev/growth-actions" element={<React.Suspense fallback={null}><GrowthActionsHarness /></React.Suspense>} />}
            {ServerGtmHarness && <Route path="/__dev/server-gtm" element={<React.Suspense fallback={null}><ServerGtmHarness /></React.Suspense>} />}
            {OrdersHarness && <Route path="/__dev/orders" element={<React.Suspense fallback={null}><OrdersHarness /></React.Suspense>} />}
            {TodayHarness && <Route path="/__dev/today" element={<React.Suspense fallback={null}><TodayHarness /></React.Suspense>} />}
            {ValidationHarness && <Route path="/__dev/validation" element={<React.Suspense fallback={null}><ValidationHarness /></React.Suspense>} />}
            {SegmentsHarness && <Route path="/__dev/segments" element={<React.Suspense fallback={null}><SegmentsHarness /></React.Suspense>} />}
            {BuyersCardHarness && <Route path="/__dev/buyers-card" element={<React.Suspense fallback={null}><BuyersCardHarness /></React.Suspense>} />}
            {SentEventsHarness && <Route path="/__dev/sent-events" element={<React.Suspense fallback={null}><SentEventsHarness /></React.Suspense>} />}
            {GoalsHarness && <Route path="/__dev/goals" element={<React.Suspense fallback={null}><GoalsHarness /></React.Suspense>} />}
            {CampaignsHarness && <Route path="/__dev/campaigns" element={<React.Suspense fallback={null}><CampaignsHarness /></React.Suspense>} />}
            {ProductsHarness && <Route path="/__dev/products" element={<React.Suspense fallback={null}><ProductsHarness /></React.Suspense>} />}

            {/* Email verification landing - public, consumes the token. */}
            <Route path="/verify" element={<VerifyEmail />} />

            {/* Agency team invite - public, the invitee sets their password. */}
            <Route path="/accept-invite" element={<AcceptInvite />} />

            {/* Password reset landing - public, consumes the emailed token. */}
            <Route path="/reset-password" element={<ResetPassword endpoint="/api/auth/reset-password" loginHref="/app" />} />

            <Route
              path="/*"
              element={
                user ? (
                  <AuthedApp user={user} onLogout={handleLogout} />
                ) : inMemberSession() && !localStorage.getItem('token') ? (
                  // Agency member logged in but not yet in a workspace -> console.
                  <AgencyConsole />
                ) : (
                  <Login
                    onLogin={(userData) => {
                      setUser(userData);
                      localStorage.setItem('userName', userData.name);
                      localStorage.setItem('userRole', userData.role || '');
                      if (userData.permissions) localStorage.setItem('userPermissions', JSON.stringify(userData.permissions));
                      else localStorage.removeItem('userPermissions');
                    }}
                  />
                )
              }
            />
          </Routes>
          </Suspense>
        </Router>
      </QueryProvider>
    </ThemeProvider>
  );
}

export default App;
